'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';

import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { motion } from 'framer-motion';
import { Gift, ArrowRight, Sparkles } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getTranslation, type Locale } from '@/lib/i18n';

const UNIQUE_PAGES_THRESHOLD = 8;
const STORAGE_KEY = 'open_visited_pages';
const MODAL_SHOWN_KEY = 'open_gift_modal_shown';
const MODAL_SUBMITTED_KEY = 'open_gift_modal_submitted';

export function GiftCardModal() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const pathname = usePathname();

  // Detect locale from pathname and get translations directly
  const isArabic = pathname.startsWith('/ar');
  const locale: Locale = isArabic ? 'ar' : 'en';
  const t = useMemo(() => getTranslation(locale), [locale]);

  // Different gift card based on locale
  const giftCardImage = isArabic
    ? '/images/homepage/500-sar-gift-card.png'
    : '/images/homepage/50-dollars-gift-card.png';
  const giftCardAlt = isArabic ? 'بطاقة هدية بقيمة ٥٠ دولار' : '$50 Gift Card';

  const checkAndShowModal = useCallback(() => {
    if (typeof window === 'undefined') return;

    // Don't show if already submitted
    const alreadySubmitted = localStorage.getItem(MODAL_SUBMITTED_KEY);
    if (alreadySubmitted) return;

    // Get visited pages from storage
    const visitedPagesJson = localStorage.getItem(STORAGE_KEY);
    const visitedPages: string[] = visitedPagesJson
      ? JSON.parse(visitedPagesJson)
      : [];

    // Add current page if not already visited
    if (!visitedPages.includes(pathname)) {
      visitedPages.push(pathname);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(visitedPages));
    }

    // Check if threshold reached and modal not shown yet
    const modalShown = localStorage.getItem(MODAL_SHOWN_KEY);
    if (visitedPages.length >= UNIQUE_PAGES_THRESHOLD && !modalShown) {
      setOpen(true);
      localStorage.setItem(MODAL_SHOWN_KEY, 'true');
    }
  }, [pathname]);

  useEffect(() => {
    // Small delay to ensure page is loaded
    const timer = setTimeout(checkAndShowModal, 1500);
    return () => clearTimeout(timer);
  }, [checkAndShowModal]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate API call - replace with actual form submission
    await new Promise((resolve) => setTimeout(resolve, 1200));

    // Here you would send the email to your backend/CRM
    // Email submitted for gift card demo: email

    // Mark as submitted so modal doesn't show again
    localStorage.setItem(MODAL_SUBMITTED_KEY, 'true');

    setIsSubmitting(false);
    setIsSubmitted(true);

    // Redirect to demo page after showing success
    setTimeout(() => {
      window.location.href = isArabic
        ? '/ar/demo?gift_card=true'
        : '/demo?gift_card=true';
    }, 2000);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="overflow-hidden p-0 sm:max-w-[480px]"
        dir={isArabic ? 'rtl' : 'ltr'}
      >
        {/* Hero Section with Gift Card - Full Coverage Image */}
        <div className="relative h-56 w-full overflow-hidden">
          {/* Full coverage background image */}
          <Image
            src={giftCardImage}
            alt={giftCardAlt}
            fill
            className="object-cover object-center"
            priority
          />

          {/* Subtle gradient overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/20" />

          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden">
            <motion.div
              className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-emerald-500/15 blur-3xl"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.2, 0.4, 0.2],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
            <motion.div
              className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-blue-500/15 blur-3xl"
              animate={{
                scale: [1.2, 1, 1.2],
                opacity: [0.2, 0.4, 0.2],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 2,
              }}
            />
          </div>

          {/* Top Badge */}

          {/* Sparkle decorations */}
          <motion.div
            className="absolute top-4 right-12 z-10"
            animate={{ rotate: 360, scale: [1, 1.2, 1] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
          >
            <Sparkles className="h-5 w-5 text-yellow-400/80" />
          </motion.div>
          <motion.div
            className="absolute right-8 bottom-6 z-10"
            animate={{ rotate: -360, scale: [1, 1.3, 1] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
          >
            <Sparkles className="h-4 w-4 text-white/60" />
          </motion.div>
        </div>

        {/* Content Section */}
        <div className="p-6">
          {!isSubmitted ? (
            <>
              <DialogHeader
                className={`mb-6 ${isArabic ? 'items-start text-right' : 'text-center'}`}
              >
                <DialogTitle className="text-2xl font-bold tracking-tight">
                  {t.giftCardModal['Get a $50 Gift Card']}
                </DialogTitle>
                <DialogDescription className="mt-2 text-base text-zinc-600">
                  {
                    t.giftCardModal[
                      'Schedule a personalized demo with our team and receive a $50 gift card as a thank you.'
                    ]
                  }
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label
                    htmlFor="demo-email"
                    className={`text-sm font-medium ${isArabic ? 'block text-right' : ''}`}
                  >
                    {t.giftCardModal['Work email']}
                  </Label>
                  <Input
                    id="demo-email"
                    type="email"
                    placeholder={t.giftCardModal['you@company.com']}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12"
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="h-12 w-full bg-black text-base font-semibold hover:bg-zinc-800"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <motion.div
                      className="flex items-center gap-2"
                      animate={{ opacity: [1, 0.5, 1] }}
                      transition={{ duration: 1, repeat: Infinity }}
                    >
                      <Gift className="h-4 w-4" />
                      {t.giftCardModal['Scheduling...']}
                    </motion.div>
                  ) : (
                    <>
                      {t.giftCardModal['Schedule Demo & Claim Gift Card']}
                      <ArrowRight
                        className={`h-4 w-4 ${isArabic ? 'mr-2 rotate-180' : 'ml-2'}`}
                      />
                    </>
                  )}
                </Button>

                <p
                  className={`text-xs text-zinc-500 ${isArabic ? 'text-right' : 'text-center'}`}
                >
                  {t.giftCardModal['By submitting, you agree to our']}{' '}
                  <Link
                    href={isArabic ? '/ar/incentive-terms' : '/incentive-terms'}
                    className="text-zinc-700 underline underline-offset-2 hover:text-black"
                    target="_blank"
                  >
                    {t.giftCardModal['Incentive Campaign Terms']}
                  </Link>
                  .
                </p>
              </form>
            </>
          ) : (
            <motion.div
              className="py-8 text-center"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
              >
                <Gift className="h-8 w-8 text-emerald-600" />
              </motion.div>
              <h3 className="mb-2 text-xl text-zinc-900">
                {t.giftCardModal["You're all set!"]}
              </h3>
              <p className="text-zinc-600">
                {t.giftCardModal['Redirecting you to schedule your demo...']}
              </p>
            </motion.div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Provider component to wrap the app
export function GiftCardModalProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <>
      {children}
      <GiftCardModal />
    </>
  );
}
